import {AbstractControl, FormControlStatus, FormGroup} from "@angular/forms";
import {BehaviorSubject, combineLatest, Observable, of, Subject} from "rxjs";
import {StandardName} from "../../store/state/order.state";
import {EventEmitter} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {catchError, delayWhen, distinct, distinctUntilChanged, filter, first, map, mergeMap, reduce, takeWhile, tap, timeout} from "rxjs/operators";
import {STANDARD_ERROR_MAP} from "./standard-error-map";
import {environment} from "../../../environments/environment";

export abstract class FormService {

	protected submitClicked: boolean;
	protected subFormInitializedMap: { [formKey: string]: boolean } = {};
	protected subFormMap: { [formKey: string]: FormGroup } = {};
	private submitClicked$: EventEmitter<any> = new EventEmitter<any>();
	private subFormValid$: BehaviorSubject<FormValidity> = new BehaviorSubject({});
	private inputsEnabled$: Subject<boolean> = new BehaviorSubject(true);
	private formFinalized$: Subject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly debug: boolean;
	private formValid$: Observable<boolean> = of(true).pipe(
		delayWhen(() => this.formFinalized$.pipe(first(formFinalized => formFinalized))),
		timeout(5000),
		catchError(err => {
			console.error('Error occurred in ', this);
			return of(false);
		}),
		map(() => this.getAllFormKeys().map(formKey => this.isSubFormValid(formKey))),
		mergeMap(formValidObservables => combineLatest(formValidObservables).pipe(map(formsValidity => {
			this.debug && console.info("Checking forms validity: ", formsValidity, this);
			return formsValidity.every(valid => !!valid)
		})))
	);

	constructor(protected translate: TranslateService) {
		this.debug = environment.showFormServiceLogs;
		if (environment.measureFormSubmitTime) {
			this.measureIsFormSubmitInvocationOnSubmit();
		}
	}

	buildSubForm(formKey: string, totalSubForms: number, ...args: any[]): FormGroup {
		const form = this.buildFormFor(formKey, args);
		this.subFormMap[formKey] = form
		this.markAsInitialized(formKey, this.getAllFormKeys().length == totalSubForms);
		return form;
	}

	getForm(formKey: string): FormGroup {
		return this.subFormMap[formKey];
	}

	getAllFormKeys(): string[] {
		return Object.keys(this.subFormMap);
	}

	isSubFormInitialized(formKey: string): boolean {
		return this.subFormInitializedMap[formKey] === true;
	}

	isFormInitialized(): boolean {
		let formKeys = this.getAllFormKeys();
		return formKeys.length > 0 && formKeys.every(formKey => this.isSubFormInitialized(formKey));
	}

	getFormControl(formKey: string, ...path: string[]): AbstractControl {
		let controlName = path[path.length - 1];
		controlName = controlName.endsWith(formKey) ? controlName : controlName + formKey;
		path[path.length - 1] = controlName;
		return this.getForm(formKey).get(path);
	}

	getAllControls(formKey: string): { [key: string]: AbstractControl } {
		const form = this.getForm(formKey);
		return form ? form.controls : {};
	}

	/**
	 * Returns names of all controls contained in this form service (even if they are not visible on page), in order in which they appear on the screen
	 */
	abstract getControlNamesInOrder(): string[];

	onSubmitRequestedByParentFormService(): Observable<boolean> {
		this.submitClicked = true;
		this.submitClicked$.emit();
		this.debug && console.info("onSubmitRequestedByParentFormService(): Triggering validations", this);
		this.triggerValidations();
		return this.isFormValid().pipe(first(), tap(valid => this.debug && console.info("onSubmitRequestedByParentFormService(): is form valid?", valid, this)));
	}

	submit(submitAction: () => void, onValidationsFailedAction?: () => void, additionalSubmitCheck?: () => boolean, finalizeAction?: () => void): void {
		this.measureTime('FormService.submit', () => {
			this.debug && console.info('Calling submit on form service:', this);
			this.submitClicked = true;
			this.submitClicked$.emit();
			this.triggerValidations();
			this.isFormValid().pipe(first()).subscribe(valid => {
				if (valid && (!!additionalSubmitCheck ? additionalSubmitCheck() : true)) {
					this.debug && console.info('Form valid, blocking inputs & invoking submit action:', this);
					this.blockInputs();
					this.measureTime('FormService.submitAction', () => submitAction());
				} else {
					this.debug && console.info('Form invalid, NOT invoking submit action:', this);
					!!onValidationsFailedAction && onValidationsFailedAction();
				}

				!!finalizeAction && this.measureTime('FormService.finalizeAction', () => finalizeAction());
			});
		});
	}

	inputsEnabled(): Observable<boolean> {
		return this.inputsEnabled$.asObservable();
	}

	triggerValidations(): void {
		this.getAllFormKeys().forEach(formKey => this.triggerSubFormValidations(formKey));
	}

	triggerSubFormValidations(formKey: string): void {
		if (this.isSubFormInitialized(formKey)) {
			Object.keys(this.getAllControls(formKey)).forEach(controlName => {
				const formControl = this.getFormControl(formKey, controlName);
				formControl?.markAsDirty();
				formControl?.markAsTouched();
				formControl?.updateValueAndValidity();
			});
		}
	}

	triggerValidationsFor(formKey: string, ...formControlNames: string[]): void {
		formControlNames.forEach(formControlName => {
			const formControl = this.getFormControl(formKey, formControlName);
			formControl.markAsDirty();
			formControl.markAsTouched();
			formControl.updateValueAndValidity();
		});
	}

	isSubFormValid(formKey: string): Observable<boolean> {
		return this.subFormValid$.asObservable().pipe(
			filter(validity => !this.isSubFormInitialized(formKey) || validity[formKey] != null),
			tap(validity => this.debug && console.info('Is sub form valid?', validity, this)),
			map(validity => this.isSubFormInitialized(formKey) && validity[formKey]),
		);
	}

	isSubFormValidOrPristine(formKey: string): Observable<boolean> {
		return this.isSubFormValid(formKey).pipe(map(valid => valid || this.getForm(formKey).pristine));
	}

	shouldShowMainValidationMessage(): boolean {
		return this.isFormInitialized() ?
			this.submitClicked && this.getAllFormKeys().some(formKey => {
				return this.getForm(formKey).status == 'INVALID';
			}) :
			false;

	}

	isFormValid(): Observable<boolean> {
		return this.formValid$;
	}

	isFormDirty(): boolean {
		return this.getAllFormKeys().map(formKey => this.isSubFormInitializedAndDirty(formKey)).reduce((f1, f2) => f1 || f2, false);
	}

	isSubFormPristine(formKey: string) {
		const form = this.getForm(formKey);
		return form ? form.pristine : true;
	}

	isInvalid(formKey: string, ...path: string[]) {
		const control = this.getFormControl(formKey, ...path);
		return control && control.invalid && !control.pristine;
	}

	blockInputs() {
		this.inputsEnabled$.next(false);
		this.getAllFormKeys().forEach(formKey => this.blockSubFormInputs(formKey));
	}

	blockSubFormInputs(formKey: string) {
		for (const formControl in this.getAllControls(formKey)) {
			this.getFormControl(formKey, formControl).disable();
		}
	}

	blockInput(formKey: string, formControlName: string) {
		this.getFormControl(formKey, formControlName).disable();
	}

	enableInputs() {
		this.inputsEnabled$.next(true);
		this.getAllFormKeys().forEach(formKey => this.enableSubFormInputs(formKey));
	}

	enableSubFormInputs(formKey: string) {
		// tslint:disable-next-line:forin
		for (const formControl in this.getAllControls(formKey)) {
			this.getFormControl(formKey, formControl).enable();
		}
	}

	updateFormControl(formControlName: string, value: any, formKey: string) {
		this.getFormControl(formKey, formControlName).setValue(value);
	}

	getErrorsString(formKey: string, ...path: string[]): Observable<string> {
		let errors = this.getErrors(formKey, ...path);
		if (errors != null) {
			return errors.pipe(distinct(), reduce((e1, e2, index) => {
				return (index == 0 ? '' : e1 + (e1.length == 0 ? '' : ', ')) + e2;
			}, ''));
		}
		return null;
	}

	getErrorsIfInvalid(formKey: string, formControlName: string): Observable<string> {
		return this.getFormControl(formKey, formControlName) && this.isInvalid(formKey, formControlName) ?
			this.getErrorsString(formKey, formControlName) :
			of('');
	}

	getValueFor(controlName: string, formKey: string): any {
		return this.getFormControl(formKey, controlName).value;
	}

	getValueOrNull(formKey: string, formControlName: string): any {
		if (this.getFormControl(formKey, formControlName) != null) {
			return this.getValueFor(formControlName, formKey);
		}
		return null;
	}

	onValueChange<T>(formControlName: string, formKey: string): Observable<T> {
		return this.getFormControl(formKey, formControlName).valueChanges;
	}

	onStatusChange(formControlName: string, formKey: string): Observable<FormControlStatus> {
		return this.getFormControl(formKey, formControlName).statusChanges;
	}

	clearAllData(formKey: string) {
		const form = this.getForm(formKey);
		form && Object.keys(form.controls).forEach(controlName => this.clearFormControl(formKey, controlName));
	}

	destroy() {
		this.submitClicked = false;
		this.formFinalized$.next(false);
		this.inputsEnabled$.next(true);
		this.getAllFormKeys().filter(formKey => this.isSubFormInitialized(formKey)).forEach(key => this.destroySubForm(key));
	}

	destroySubForm(formKey: string) {
		this.subFormInitializedMap[formKey] = false;
		delete this.subFormMap[formKey];
		this.subFormValid$.next({})
	}

	getSubFormControlNames(formKey: string): string[] {
		if (this.isSubFormInitialized(formKey)) {
			return Object.keys(this.getForm(formKey).controls);
		}

		return [];
	}

	copyAnswers(from: StandardName, to: StandardName): void {
		const srcControlNames: string[] = this.getSubFormControlNames(from).map(name => name.replace(from, ''));
		srcControlNames.forEach(controlName => this.tryCopyAnswer(controlName, from, to));
	}

	onSubmitClicked$(): Observable<any> {
		return this.submitClicked$.asObservable();
	}

	getFirstInvalidControl(): string | null {
		let invalidControls = this.getAllFormKeys().map(formKey => this.firstInvalidControl(formKey)).filter(control => control != null);
		return invalidControls.length > 0 ? invalidControls[0] : null;

	}

	hasValue(control: AbstractControl) {
		return !this.hasNoValue(control);
	}

	hasNoValue(control: AbstractControl) {
		if (control.value != null) {
			if (typeof control.value === "string") {
				return (control.value as string).trim().length === 0;
			} else if (Array.isArray(control.value)) {
				return (control.value as any[]).length === 0;
			} else {
				return false;
			}
		}

		return true;
	}

	getRawErrors(formKey: string, ...path: string[]): string[] {
		const control = this.getFormControl(formKey, ...path);
		if (control.errors == null) {
			return null;
		}
		return Object.keys(control.errors).map(errorKey => {
			return this.resolveTranslationKey(this.getFormSpecificErrorMap(), errorKey, path[path.length - 1]) ||
				this.resolveTranslationKey(this.getErrorMessageMap(), errorKey, path[path.length - 1]) ||
				errorKey;
		}).filter(key => key != null);
	}

	getErrors(formKey: string, ...path: string[]): Observable<string[]> {
		let rawErrors = this.getRawErrors(formKey, ...path);
		return rawErrors == null ?
			of([]) :
			this.translate.get(rawErrors).pipe(mergeMap(obj => Object.keys(obj).map(key => obj[key])));
	}

	protected getFormName(): string {
		return this.constructor.name;
	}

	protected markAsInitialized(formKey: string, isLastSubForm: boolean) {
		this.subFormInitializedMap[formKey] = true;
		if (isLastSubForm) {
			this.formFinalized$.next(true);
		}

		this.getForm(formKey).statusChanges.pipe(
			takeWhile(() => !!this.subFormInitializedMap[formKey]),
			tap(status => this.debug && console.info("Sub-form " + formKey + " emits status change: " + status, this)),
			filter(status => ["INVALID", "VALID"].includes(status)),
			map(status => status === "VALID"),
			distinctUntilChanged()
		).subscribe(valid => {
			let subFormValidity = {};
			subFormValidity[formKey] = valid;
			this.subFormValid$.next(Object.assign(this.subFormValid$.getValue(), subFormValidity));
		});
	}

	protected isSubFormInitializedAndDirty(formKey: string): boolean {
		return this.isSubFormInitialized(formKey) && this.getForm(formKey).dirty;
	}

	protected tryCopyAnswer(controlName: string, srcForm: string, dstForm: string, isCheckbox: boolean = false): void {
		const srcControl = this.getFormControl(srcForm, controlName);
		const dstControl = !!this.getForm(dstForm) ? this.getFormControl(dstForm, controlName) : null;

		if (this.canCopy(srcControl, dstControl, isCheckbox)) {
			dstControl.setValue(srcControl.value);
			this.triggerValidationsFor(dstForm, controlName);
		}
	}

	protected canCopy(srcControl: AbstractControl, dstControl: AbstractControl, isCheckbox: boolean = false) {
		return dstControl != null && this.hasValue(srcControl) && (this.hasNoValue(dstControl) || isCheckbox && dstControl.value === false);
	}

	protected getFormSpecificErrorMap(): { [validationKey: string]: ValidationMsg[] } {
		return {};
	}

	protected abstract buildFormFor(formKey: string, ...args: any[]);

	private measureIsFormSubmitInvocationOnSubmit() {
		this.submitClicked$.pipe(first()).subscribe(() => {
			let start = Date.now();
			this.formValid$.pipe(map(() => {
				let interval = Date.now() - start;
				start = Date.now();
				return interval;
			})).subscribe(interval => console.info(`FormService.isFormValid; ${this.getFormName()}; duration [ms] = ${interval}`));
		});
	}

	private getErrorMessageMap(): { [validationKey: string]: ValidationMsg[] } {
		return STANDARD_ERROR_MAP;
	}

	private resolveTranslationKey(errorMap: { [p: string]: ValidationMsg[] }, errorKey: string, formControlName: string | null): string | null {
		if (errorMap[errorKey]) {
			const validationMsg: ValidationMsg = errorMap[errorKey].find(vm => vm.formControlName === formControlName) ||
				errorMap[errorKey].find(vm => vm.formControlName == null);
			return validationMsg ? validationMsg.msg : null;
		}

		return null;
	}

	private firstInvalidControl(formKey: string): string | null {
		let allControls = this.getControlNamesInOrder();
		let invalidControls = allControls.filter(controlName => this.isInvalid(formKey, controlName));
		return invalidControls.length > 0 ? invalidControls[0] : null;
	}

	private clearFormControl(formKey: string, controlName: string) {
		this.getFormControl(formKey, controlName).reset(null);
	}

	private measureTime(message: string = '', action: () => void): void {
		if (environment.measureFormSubmitTime) {
			let start = Date.now();
			action();
			let end = Date.now();
			console.info(`${this.getFormName()} ${';' + message}; duration [ms] = ${end - start}`)
		} else {
			action();
		}
	}
}

export type FormValidity = { [formKey: string]: boolean };

export interface ValidationMsg {
	formControlName?: string;
	msg: string;
}
